import { AxiosResponse } from 'axios'
import $api from '../http'

export default class PaymentService {
  static async getPaymentConfig(): Promise<AxiosResponse> {
    return $api.get('/payments/get_payment_config')
  }

  // Catalogo pacchetti crediti (unica fonte server-side: services/pricing.py)
  static async getPackages(): Promise<AxiosResponse> {
    return $api.get('/payments/packages')
  }

  // Piano attivo, quote e moduli. Il prezzo puo' essere null: il listino non
  // esiste ancora (routes/payments_fastapi.py::get_subscription).
  static async getSubscription(): Promise<AxiosResponse> {
    return $api.get('/payments/subscription')
  }

  // Carrello moduli. Il server risponde {status:'redirect', url} al primo acquisto
  // (Stripe Checkout hosted) o {status:'activated'} quando aggiunge l'item a una
  // subscription attiva. Con Managed Payments acceso risponde invece
  // {status:'embedded', clientSecret, publishableKey}: una Checkout incorporata,
  // un abbonamento per modulo. L'attivazione vera arriva dal webhook: la UI la attende.
  static async checkoutModule(moduleId: string): Promise<AxiosResponse> {
    return $api.post(
      `/payments/modules/${encodeURIComponent(moduleId)}/checkout`
    )
  }

  // Disattivazione: {status:'deactivating'}; il modulo si spegne al webhook.
  // Un modulo comprato con Managed Payments si disdice a fine periodo:
  // {status:'scheduled', endsAt} (epoch in secondi), resta acceso fino ad allora.
  static async deactivateModule(moduleId: string): Promise<AxiosResponse> {
    return $api.post(
      `/payments/modules/${encodeURIComponent(moduleId)}/deactivate`
    )
  }

  // Piani in self-service con Managed Payments (spec digiscan-web 2026-10-26). Il
  // client manda solo il nome del piano. Acquisto: {status:'embedded', clientSecret,
  // publishableKey}; il tier cambia quando Stripe conferma il pagamento.
  static async checkoutPlan(tier: string): Promise<AxiosResponse> {
    return $api.post(`/payments/plans/${encodeURIComponent(tier)}/checkout`)
  }

  // Cambio: {status:'changed'|'pending', tier} per un piano superiore (subito, se il
  // pagamento riesce), {status:'scheduled', tier, endsAt} per uno inferiore (a fine periodo).
  static async changePlan(tier: string): Promise<AxiosResponse> {
    return $api.post(`/payments/plans/${encodeURIComponent(tier)}/change`)
  }

  // Disdetta a fine periodo: {status:'scheduled', endsAt} (epoch in secondi).
  static async cancelPlan(): Promise<AxiosResponse> {
    return $api.post('/payments/plans/cancel')
  }

  // Checkout incorporata con Managed Payments (spec digiscan-web 2026-10-22):
  // il client manda solo i crediti del pacchetto e la propria origine, prezzo e
  // importo li decide il server. {sessionId, clientSecret, publishableKey}.
  static async createCreditsCheckout(
    credits: number,
    returnOrigin: string
  ): Promise<AxiosResponse> {
    return $api.post('/payments/checkout/credits', { credits, returnOrigin })
  }

  // Pagina di ritorno: il server rilegge la sessione da Stripe e la evade, con lo
  // stesso accredito idempotente del webhook. {kind, result, credits, moduleId}.
  static async confirmCheckout(sessionId: string): Promise<AxiosResponse> {
    return $api.post('/payments/checkout/confirm', { sessionId })
  }

  static async createPaymentIntent(
    email: string,
    credits: number
  ): Promise<AxiosResponse> {
    return $api.post('/payments/create_payment_intent', { email, credits })
  }

  static async getPaymentDetails(
    paymentIntent: string
  ): Promise<AxiosResponse> {
    return $api.post('/payments/get_payment_details', { paymentIntent })
  }

  // Il server verifica il PaymentIntent contro Stripe e deriva crediti/importo
  // server-side: serve inviare solo l'id del PaymentIntent.
  static async addCredits(pid: string): Promise<AxiosResponse> {
    return $api.post('/payments/add_credits', { pid })
  }

  static async getCredits(email: string): Promise<AxiosResponse> {
    return $api.post('/payments/get_credits', {
      email,
    })
  }

  // Consumato, saldo, totale e consumo giornaliero dell'utente del token,
  // dal ledger dei crediti (prima la scheda Consumption mostrava numeri finti).
  static async getConsumptionSummary(): Promise<AxiosResponse> {
    return $api.get('/payments/get_consumption_summary')
  }

  // Fatture e ricevute del fornitore dei pagamenti (solo sul backend centrale).
  static async getInvoices(): Promise<AxiosResponse> {
    return $api.get('/payments/invoices')
  }

  static async getPaymentHistory(email: string): Promise<AxiosResponse> {
    return $api.post('/payments/get_payment_history', {
      email,
    })
  }
}
